import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DashboardFiltersBar, DashboardFilters } from "../../components/DashboardFiltersBar";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { getCurrentMonthRange } from "../../utils/dateUtils";

type GovData = {
  audit?: {
    total: number;
    byUser: Array<{ userId: number | null; _count: number }>;
    criticalEvents: Array<{ id: number; event: string; createdAt: string }>;
    quality: { missingBefore: number; missingAfter: number };
  };
};

type AuditDetail = {
  id: number;
  event: string;
  resourceType?: string | null;
  resourceId?: string | null;
  userId?: number | null;
  createdAt: string;
};

export const GovDashboardPage: React.FC = () => {
  const { client } = useAuth();
  const [filters, setFilters] = useState<DashboardFilters>(() => getCurrentMonthRange());
  const [data, setData] = useState<GovData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const response = await client.get(`/dash/gov?${query}`);
      if (!response.ok) {
        console.error("Gov dashboard failed", { status: response.status });
        setData(null);
        setError("Falha ao carregar dashboard.");
        setLoading(false);
        return;
      }
      setData((await response.json()) as GovData);
      setLoading(false);
    };
    fetchData();
  }, [client, query]);

  const auditDetailQuery = usePagedQuery<AuditDetail>({
    endpoint: "/dash/gov/auditoria",
    filters,
  });

  const criticalSeries = useMemo(() => {
    const events = data?.audit?.criticalEvents ?? [];
    const buckets = new Map<string, number>();
    events.forEach((event) => {
      const day = event.createdAt.slice(0, 10);
      buckets.set(day, (buckets.get(day) ?? 0) + 1);
    });
    return Array.from(buckets.entries()).map(([date, total]) => ({
      date,
      total,
    }));
  }, [data?.audit?.criticalEvents]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Dashboard Governanca</h1>
      <DashboardFiltersBar value={filters} onChange={setFilters} />
      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="row g-3">
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted">Total de eventos</h6>
              <h3>{data?.audit?.total ?? 0}</h3>
              <p className="text-muted">
                Missing before: {data?.audit?.quality?.missingBefore ?? 0}
              </p>
              <p className="text-muted">
                Missing after: {data?.audit?.quality?.missingAfter ?? 0}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted">Ranking por usuario</h6>
              <ul className="list-group list-group-flush">
                {(data?.audit?.byUser ?? []).map((item) => (
                  <li
                    className="list-group-item d-flex justify-content-between"
                    key={item.userId ?? "null"}
                  >
                    <span>{item.userId ?? "Anonimo"}</span>
                    <span>{item._count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-lg-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Eventos criticos no periodo</h5>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={criticalSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="total" stroke="#0d6efd" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <PageState loading={auditDetailQuery.loading} error={auditDetailQuery.error}>
          <DataTable<AuditDetail>
            title="Auditoria detalhada"
            rows={auditDetailQuery.rows}
            total={auditDetailQuery.total}
            page={auditDetailQuery.page}
            take={auditDetailQuery.take}
            onPageChange={auditDetailQuery.setPage}
            columns={[
              { label: "Evento", render: (item) => item.event },
              { label: "Recurso", render: (item) => item.resourceType ?? "-" },
              { label: "ID", render: (item) => item.resourceId ?? "-" },
              { label: "Usuario", render: (item) => item.userId ?? "-" },
              { label: "Data", render: (item) => item.createdAt },
            ]}
          />
        </PageState>
      </div>
    </div>
  );
};
